/**
 * Les chaînes de l'interface, français et anglais.
 *
 * L'anglais est britannique : le Royaume-Uni pèse 60 500 recherches mensuelles
 * sur « darts scorer » contre 320 aux États-Unis sur « darts scoreboard
 * online ». Le berceau du jeu est aussi le marché de la recherche.
 *
 * Les pages de contenu ne passent pas par ici : une page anglaise est une
 * réécriture, pas une traduction de chaînes, et elle a son propre composant.
 * Ce dictionnaire ne couvre que l'application — l'écran de création et la
 * partie en cours — dont l'URL est unique et la langue choisie.
 *
 * Une clé absente de l'anglais retombe sur le français plutôt que d'afficher
 * son propre nom : un libellé français dans une interface anglaise est un
 * défaut, « game.create » à la place d'un bouton est une panne. Un test
 * compare les deux tables pour qu'aucune clé ne soit oubliée en silence.
 */

const fr = {
    // Navigation et repères
    "nav.back": "Retour",
    "load.failed": "Impossible de charger la page",
    "load.reload": "Recharger",
    "agent.recorded": "Saisi par l'assistant : {darts}",
    "agent.undoStale": "La partie a bougé depuis : annule au pavé.",
    "nav.rules": "Règles",
    "nav.rules.all": "Toutes les règles",
    "nav.rules.501": "501",
    "nav.rules.301": "301",
    "nav.rules.cricket": "Cricket",
    "nav.multiplayer": "Multijoueur",
    "nav.checkouts": "Checkouts",
    "nav.guides": "Guides",
    "nav.faq": "FAQ",
    "faq.more": "D'autres questions ?",
    "faq.seeAll": "Voir toute la FAQ",
    "keyAnswer.label": "En bref",
    "nav.play": "Jouer",
    "nav.boardSetup": "Distance et hauteur",
    "nav.stats": "Statistiques",
    "nav.training": "Entraînement",
    "nav.tournament": "Organiser un tournoi",
    "nav.appScore": "Application de score",
    "nav.blog": "Blog",
    "nav.menu.open": "Ouvrir le menu",
    "nav.menu.close": "Fermer le menu",
    "footer.col.rules": "Règles et mesures",
    "footer.col.play": "Jouer",
    "footer.col.progress": "Progresser",
    "footer.rules501": "Règles du 501",
    "footer.rules301": "Règles du 301",
    "footer.rulesCricket": "Règles du Cricket",
    "footer.checkoutTable": "Table des checkouts",
    "footer.newGame": "Créer une partie",
    "footer.multiplayer": "Jouer à plusieurs",
    "footer.tagline": "Compteur de fléchettes en ligne — 501, 301, Cricket. Gratuit, sans inscription.",
    "legal.notice": "Mentions légales",
    "legal.privacy": "Confidentialité",
    "legal.terms": "CGU",
    "brand.tagline": "Compteur de fléchettes",
    "lang.label": "Langue",
    "lang.fr": "Français",
    "lang.en": "English",
    "lang.de": "Deutsch",

    // Écran de création
    "new.title.line1": "NOUVELLE",
    "new.title.line2": "PARTIE.",
    "new.subtitle": "Crée la partie, partage le lien, chacun suit le score sur son écran.",
    "new.mode.label": "Mode de jeu",
    "new.mode.501.desc": "Décompte depuis 501",
    "new.mode.301.desc": "Version rapide",
    "new.mode.cricket.desc": "15-20 + Bull",
    "new.rules.label": "Règles des jeux",
    "new.rules.501": "Règles du 501",
    "new.rules.301": "Règles du 301",
    "new.rules.cricket": "Règles du Cricket",
    "new.players.label": "Joueurs",
    "new.players.add": "Ajouter",
    "new.players.clear": "Vider",
    "new.players.cleared": "Liste vidée",
    "new.players.undo": "Annuler",
    "new.players.move": "Déplacer le joueur {n} (flèches haut et bas)",
    "new.players.placeholder": "Joueur {n}",
    "new.advanced": "Paramètres avancés",
    "new.create": "LANCER LA PARTIE",
    "new.creating": "CRÉATION...",
    "new.noAccount": "Pas de compte. Pas d'inscription. Juste un lien.",
    "new.error.noPlayer": "Ajoute au moins un joueur",
    "new.error.duplicateNames": "Deux joueurs portent le même nom : ajoute une initiale pour les distinguer.",
    "new.error.create": "Erreur lors de la création",

    // Ecran de partie
    "game.error.connection": "Connexion impossible",
    "game.error.generic": "Erreur",
    "game.error.notFound": "Partie introuvable",
    "game.notFound.title": "PARTIE INTROUVABLE",
    "game.backHome": "RETOUR ACCUEIL",
    "game.leaveLabel": "Quitter la partie, retour à l'accueil",
    "game.error.nothingToUndo": "Rien à annuler",
    // Erreurs metier renvoyees par l'API, par code (voir lib/apiError.js)
    "api.min_one_player": "Ajoute au moins un joueur",
    "api.game_not_found": "Partie introuvable",
    "api.game_over": "Partie déjà terminée",
    "api.invalid_dart": "Valeur de fléchette invalide",
    "api.invalid_bull": "Bull ne peut être que simple (25) ou double (50)",
    "api.invalid_multiplier": "Multiplicateur invalide",
    "api.not_your_turn": "Ce n'est pas le tour de ce joueur ({name} doit jouer)",
    "api.nothing_to_undo": "Rien à annuler",
    "game.menu": "Options de la partie",
    "game.copyLink": "Copier le lien",
    "game.spectator": "Affichage spectateur",
    "game.scoring": "Affichage de saisie",
    "spectator.autoJoin": "La revanche s'ouvre d'elle-même dans {n} secondes.",
    "spectator.autoJoinSolo": "La partie suivante s'ouvre d'elle-même dans {n} secondes.",
    "game.linkCopied": "Lien copié !",
    "game.copyFailed": "Impossible de copier",
    "game.otherPlayers": "Autres joueurs",
    "game.showStats": "Voir les stats",
    "game.close": "Fermer",
    "game.nextOrder": "Ordre du prochain tour",
    "game.next": "PROCHAIN",
    "game.points": "POINTS",
    "game.score": "SCORE",
    "game.wakeLock": "ANTI-VEILLE",
    "game.nextIs": "Prochain : {name}",
    "keypad.single": "SIMPLE",
    "keypad.double": "DOUBLE",
    "keypad.triple": "TRIPLE",
    "keypad.hint": "Choisis le multiplicateur puis tape sur la cible.",
    "keypad.undo": "ANNULER",
    "live.dart": "{dart}, reste {left}.",
    "live.miss": "raté",
    "live.triple": "triple {n}",
    "live.double": "double {n}",
    "live.bull25": "bull 25",
    "live.bull50": "bull 50",
    "live.undone": "Fléchette annulée.",
    "live.volley": "{name} : {total}, reste {left}.",
    "live.volleyCricket": "{name} a fini sa volée.",
    "live.bust": "{name} : bust, score inchangé.",
    "live.next": "À {name}, reste {left}.",
    "live.nextCricket": "À {name}.",
    "dart.miss": "RATÉ",
    "stats.mpr": "MPR",
    "board.turnOf": "Tour de",
    "board.visit": "Volée",
    "board.target": "Cible",
    "board.points": "Pts",
    "board.noMark": "aucune marque",
    "board.oneMark": "1 marque",
    "board.marks": "{n} marques",
    "board.closed": "fermé",
    "board.closedByAll": "fermé par tous",
    "board.bull": "bull",
    "stats.title": "Statistiques",
    "stats.live": "Mises à jour à chaque volée",
    "stats.avg3": "moy/3",
    "stats.best": "best",
    "stats.darts": "darts",
    "recap.bust": "Bust",
    "recap.undo": "ANNULER",
    "recap.next": "SUIVANT",
    "recap.nextTurn": "Au tour de",
    "winner.wins": "remporte la partie.",
    "winner.rematch": "REVANCHE",
    "winner.rematchJoin": "REJOINDRE LA REVANCHE",
    "winner.replay": "REJOUER",
    "winner.replayJoin": "PARTIE SUIVANTE",
    "winner.newGame": "NOUVELLE PARTIE",
    "recap.done": "Volée terminée",
    "recap.undone": "Volée annulée",
    "recap.total": "Total volée",
    "recap.score": "Score",
    "winner.title": "Victoire",
    "winner.stats": "Statistiques",
    "winner.avg3": "Moy. 3 fl.",
    "winner.darts": "Fléchettes",
    "winner.best": "Meilleure",
    "winner.showLess": "Réduire",
    "winner.showMoreOne": "Voir 1 autre",
    "winner.showMoreMany": "Voir {n} autres",
    "winner.bookmark": "Ajouter aux favoris",
    "winner.bookmarkShare": "Ajoute DartSync à tes favoris.",
    "winner.bookmarkMac": "Appuie sur ⌘ + D pour ajouter cette page aux favoris.",
    "winner.bookmarkPc": "Appuie sur Ctrl + D pour ajouter cette page aux favoris.",
    "winner.bookmarkIos": "Sur iPhone/iPad : appuie sur Partager, puis sur Ajouter aux favoris.",
    "winner.bookmarkAndroid": "Sur Android : ouvre le menu du navigateur puis choisis Ajouter aux favoris.",
    "winner.bookmarkMobile": "Sur mobile, ouvre le menu du navigateur puis ajoute cette page aux favoris.",
    "winner.feedback": "Ça t'a plu ? Ton avis m'aide à améliorer DartSync.",
    "feedback.footer": "Donner mon avis",
    "feedback.celebrate": "Noter DartSync",
    "feedback.title": "Ton avis",
    "feedback.question": "Tu as aimé DartSync ?",
    "feedback.up": "J'ai aimé",
    "feedback.down": "Je n'ai pas aimé",
    "feedback.commentPlaceholder": "Un mot de plus ? (facultatif)",
    "feedback.send": "Envoyer",
    "feedback.sent": "Merci, c'est bien reçu.",
    "feedback.error": "L'envoi a échoué. Réessaie dans un instant.",
    "feedbackB.celebrate": "Comment était ta partie ?",
    "feedbackB.hint": "Dis-moi si le compteur a fait le job.",
    "feedbackB.title": "Ta partie",
    "feedbackB.question": "Le compteur t'a simplifié la partie ?",
    "feedbackB.up": "Oui",
    "feedbackB.down": "Pas vraiment",
    "feedbackB.commentPlaceholder": "Un mot sur ta partie ? (facultatif)",
    "feedbackB.commentPlaceholderUp": "Qu'est-ce qui t'a plu ? (facultatif)",
    "feedbackB.commentPlaceholderDown": "Qu'est-ce qui a coincé ? (facultatif)",
    "feedbackB.send": "Envoyer",
    "feedbackB.sent": "Merci, bien noté.",
    "feedbackB.error": "L'envoi a échoué. Réessaie dans un instant.",
    "legal.updated": "Dernière mise à jour :",

    "settings.close": "Fermer",
    "settings.stored": "Mémorisés sur cet appareil et réutilisés pour tes prochaines parties.",
    "startOrder.noHistory": "Aucune partie précédente : l'ordre de la liste est gardé.",
    "checkout.hint": "Comment finir la partie à exactement 0.",

    // Ordre de départ
    "startOrder.label": "Ordre de passage",
    "startOrder.hint": "Qui commence la partie.",
    "startOrder.preview": "Ordre au lancement :",
    "startOrder.previewRandom": "Ordre tiré au sort au lancement.",
    "startOrder.fixed.label": "Saisie",
    "startOrder.fixed.chip": "SAISIE",
    "startOrder.fixed.desc": "L'ordre de la liste ci-dessus",
    "startOrder.random.label": "Aléatoire",
    "startOrder.random.chip": "ALÉATOIRE",
    "startOrder.random.desc": "Tirage au sort à chaque lancement",
    "startOrder.reverse.label": "Perdant d'abord",
    "startOrder.reverse.chip": "PERDANT",
    "startOrder.reverse.desc": "Le dernier de la partie précédente commence",
    "startOrder.alternate.label": "Alternance",
    "startOrder.alternate.chip": "ROTATION",
    "startOrder.alternate.desc": "On décale d'un joueur à chaque partie",

    // Règle de sortie
    "checkout.label": "Règle de sortie",
    "checkout.double_out.label": "Double out",
    "checkout.double_out.chip": "DOUBLE",
    "checkout.double_out.desc": "Finir sur un double (ou le bull 50). Standard.",
    "checkout.master_out.label": "Master out",
    "checkout.master_out.chip": "MASTER",
    "checkout.master_out.desc": "Finir sur un double ou un triple.",
    "checkout.straight_out.label": "Straight out",
    "checkout.straight_out.chip": "STRAIGHT",
    "checkout.straight_out.desc": "Finir sur n'importe quel numéro.",

    // Variante du Cricket
    "cricket.variant.label": "Variante",
    "cricket.variant.hint": "À qui vont les points marqués.",
    "cricket.standard.label": "Standard",
    "cricket.standard.chip": "STANDARD",
    "cricket.standard.desc": "Tes points s'ajoutent à ton score. Le plus haut gagne.",
    "cricket.cut_throat.label": "Cut-throat",
    "cricket.cut_throat.chip": "CUT-THROAT",
    "cricket.cut_throat.desc": "Tes points vont aux adversaires qui n'ont pas fermé le numéro. Le plus bas gagne.",
    "board.lowestWins": "Le plus bas gagne",
    "recap.inflicted": "points infligés au total !",

    // Limite de tours et egalite
    "rounds.label": "Nombre de tours",
    "rounds.hint": "Un tour, c'est une volée de chaque joueur.",
    "rounds.20.label": "20 tours",
    "rounds.20.chip": "20 TOURS",
    "rounds.20.desc": "Au bout du 20e tour, le meneur gagne.",
    "rounds.unlimited.label": "Illimité",
    "rounds.unlimited.chip": "∞",
    "rounds.unlimited.desc": "On joue jusqu'à la victoire.",
    "tie.label": "En cas d'égalité",
    "tie.hint": "Plusieurs joueurs en tête au bout du dernier tour.",
    "tie.unlimited": "Sans limite de tours, il n'y a pas d'égalité à trancher.",
    "tie.draw.label": "Match nul",
    "tie.draw.desc": "La partie s'arrête sur l'égalité.",
    "tie.overtime.label": "Départage",
    "tie.overtime.desc": "Les joueurs à égalité rejouent, tour après tour, jusqu'à ce que l'un passe devant.",
    "game.round": "TOUR {n}/{max}",
    "game.overtime": "DÉPARTAGE",
    "live.overtime": "Égalité : départage.",
    "winner.byRounds": "Aux points, au bout des {n} tours.",
    "winner.byPlayoff": "Aux points, après départage.",
    "winner.drawTitle": "Match nul",
    "winner.draw": "À égalité au bout des {n} tours.",
    "winner.lostTitle": "Perdu",
    "winner.lostLeft": "Il restait {left} points au bout des {n} tours.",
    "winner.lostOpen": "Au bout des {n} tours, cibles encore ouvertes : {targets}.",

    // Les titres d'onglet des deux ecrans de l'application. Ils ne peuvent pas
    // venir de seoConfig, qui ne porte qu'une chaine francaise par route : /new
    // affichait « Creer une partie de flechettes en ligne » sous une interface
    // allemande, et /game, absent de seoConfig, gardait le titre marketing de
    // l'accueil pendant toute la partie.
    "seo.new.title": "Créer une partie de fléchettes en ligne | DartSync",
    "seo.new.description":
        "Lancez une partie de fléchettes 501, 301 ou Cricket en quelques secondes. Partagez le lien, chaque joueur rejoint sans inscription. Score en temps réel.",
    "seo.game.title": "Partie en cours | DartSync",
    "seo.game.description":
        "Le score de la partie en cours, en temps réel sur tous les téléphones connectés.",

    // La 404. Comme /new et /game, son URL ne porte pas de langue : n'importe
    // quelle URL morte la rend, donc seoConfig — une chaine francaise par route
    // — ne peut pas la servir. Elle etait ecrite en francais en dur, ce qui
    // envoyait un lecteur allemand sur une page francaise au moindre lien casse.
    "seo.notFound.title": "Page introuvable | DartSync",
    "seo.notFound.description":
        "Cette page n'existe pas ou a été déplacée. Reviens à l'accueil pour créer une partie de fléchettes.",
    "notFound.heading": "Page introuvable",
    "notFound.body":
        "Cette page n'existe pas ou a été déplacée. Reviens à l'accueil pour créer une partie de fléchettes.",
    "notFound.back": "Retour à l'accueil",
};

const en = {
    "nav.back": "Back",
    "load.failed": "Couldn't load the page",
    "load.reload": "Reload",
    "agent.recorded": "Entered by the assistant: {darts}",
    "agent.undoStale": "The game has moved on since: undo on the keypad.",
    "nav.rules": "Rules",
    "nav.rules.all": "All the rules",
    "nav.rules.501": "501",
    "nav.rules.301": "301",
    "nav.rules.cricket": "Cricket",
    "nav.multiplayer": "Multiplayer",
    "nav.checkouts": "Checkouts",
    "nav.guides": "Guides",
    "nav.faq": "FAQ",
    "faq.more": "More questions?",
    "faq.seeAll": "See the full FAQ",
    "keyAnswer.label": "In short",
    "nav.play": "Play",
    "nav.boardSetup": "Height & distance",
    "nav.stats": "Statistics",
    "nav.training": "Practice",
    "nav.tournament": "Run a tournament",
    "nav.appScore": "Scoring app",
    "nav.blog": "Blog",
    "nav.menu.open": "Open menu",
    "nav.menu.close": "Close menu",
    "footer.col.rules": "Rules & measurements",
    "footer.col.play": "Play",
    "footer.col.progress": "Improve",
    "footer.rules501": "501 rules",
    "footer.rules301": "301 rules",
    "footer.rulesCricket": "Cricket rules",
    "footer.checkoutTable": "Checkout chart",
    "footer.newGame": "Start a game",
    "footer.multiplayer": "Play with others",
    "footer.tagline": "Online darts scorer — 501, 301, Cricket. Free, no sign-up.",
    "legal.notice": "Legal notice",
    "legal.privacy": "Privacy",
    "legal.terms": "Terms",
    "brand.tagline": "Darts scorer",
    "lang.label": "Language",
    "lang.fr": "Français",
    "lang.en": "English",
    "lang.de": "Deutsch",

    "new.title.line1": "NEW",
    "new.title.line2": "GAME.",
    "new.subtitle": "Create the game, share the link, everyone follows the score on their own screen.",
    "new.mode.label": "Game mode",
    "new.mode.501.desc": "Count down from 501",
    "new.mode.301.desc": "The quick one",
    "new.mode.cricket.desc": "15-20 + Bull",
    "new.rules.label": "Game rules",
    "new.rules.501": "501 rules",
    "new.rules.301": "301 rules",
    "new.rules.cricket": "Cricket rules",
    "new.players.label": "Players",
    "new.players.add": "Add",
    "new.players.clear": "Clear",
    "new.players.cleared": "List cleared",
    "new.players.undo": "Undo",
    "new.players.move": "Move player {n} (up and down arrows)",
    "new.players.placeholder": "Player {n}",
    "new.advanced": "Advanced settings",
    "new.create": "START GAME",
    "new.creating": "CREATING...",
    "new.noAccount": "No account. No sign-up. Just a link.",
    "new.error.noPlayer": "Add at least one player",
    "new.error.duplicateNames": "Two players have the same name: add an initial to tell them apart.",
    "new.error.create": "Could not create the game",

    // Ecran de partie
    "game.error.connection": "Connection lost",
    "game.error.generic": "Something went wrong",
    "game.error.notFound": "Game not found",
    "game.notFound.title": "GAME NOT FOUND",
    "game.backHome": "BACK TO HOME",
    "game.leaveLabel": "Leave the game, back to home",
    "game.error.nothingToUndo": "Nothing to undo",
    "api.min_one_player": "Add at least one player",
    "api.game_not_found": "Game not found",
    "api.game_over": "This game is already over",
    "api.invalid_dart": "Invalid dart value",
    "api.invalid_bull": "Bull can only be single (25) or double (50)",
    "api.invalid_multiplier": "Invalid multiplier",
    "api.not_your_turn": "It is {name}'s turn to throw",
    "api.nothing_to_undo": "Nothing to undo",
    "game.menu": "Game options",
    "game.copyLink": "Copy the link",
    "game.spectator": "Spectator view",
    "game.scoring": "Scoring view",
    "spectator.autoJoin": "The rematch opens by itself in {n} seconds.",
    "spectator.autoJoinSolo": "The next game opens by itself in {n} seconds.",
    "game.linkCopied": "Link copied",
    "game.copyFailed": "Could not copy",
    "game.otherPlayers": "Other players",
    "game.showStats": "Show stats",
    "game.close": "Close",
    "game.nextOrder": "Throwing order",
    "game.next": "NEXT",
    "game.points": "POINTS",
    "game.score": "SCORE",
    "game.wakeLock": "SCREEN AWAKE",
    "game.nextIs": "Next: {name}",
    "keypad.single": "SINGLE",
    "keypad.double": "DOUBLE",
    "keypad.triple": "TRIPLE",
    "keypad.hint": "Pick the multiplier, then tap the number.",
    "keypad.undo": "UNDO",
    "live.dart": "{dart}, {left} left.",
    "live.miss": "miss",
    "live.triple": "treble {n}",
    "live.double": "double {n}",
    "live.bull25": "outer bull",
    "live.bull50": "bull",
    "live.undone": "Dart undone.",
    "live.volley": "{name}: {total}, {left} left.",
    "live.volleyCricket": "{name}'s visit is over.",
    "live.bust": "{name}: bust, score unchanged.",
    "live.next": "{name} to throw, {left} left.",
    "live.nextCricket": "{name} to throw.",
    "dart.miss": "MISS",
    "stats.mpr": "MPR",
    "board.turnOf": "To throw",
    "board.visit": "Visit",
    "board.target": "Target",
    "board.points": "Pts",
    "board.noMark": "no mark",
    "board.oneMark": "1 mark",
    "board.marks": "{n} marks",
    "board.closed": "closed",
    "board.closedByAll": "closed by everyone",
    "board.bull": "bull",
    "stats.title": "Stats",
    "stats.live": "Updated after every visit",
    "stats.avg3": "avg",
    "stats.best": "best",
    "stats.darts": "darts",
    "recap.bust": "Bust",
    "recap.undo": "UNDO",
    "recap.next": "NEXT",
    "recap.nextTurn": "Next up:",
    "winner.wins": "wins the game.",
    "winner.rematch": "REMATCH",
    "winner.rematchJoin": "JOIN THE REMATCH",
    "winner.replay": "PLAY AGAIN",
    "winner.replayJoin": "NEXT GAME",
    "winner.newGame": "NEW GAME",
    "recap.done": "Visit complete",
    "recap.undone": "Visit undone",
    "recap.total": "Visit total",
    "recap.score": "Score",
    "winner.title": "Winner",
    "winner.stats": "Stats",
    "winner.avg3": "3-dart avg",
    "winner.darts": "Darts",
    "winner.best": "Best visit",
    "winner.showLess": "Show less",
    "winner.showMoreOne": "Show 1 more",
    "winner.showMoreMany": "Show {n} more",
    "winner.bookmark": "Add to bookmarks",
    "winner.bookmarkShare": "Bookmark DartSync.",
    "winner.bookmarkMac": "Press ⌘ + D to bookmark this page.",
    "winner.bookmarkPc": "Press Ctrl + D to bookmark this page.",
    "winner.bookmarkIos": "On iPhone or iPad: tap Share, then Add Bookmark.",
    "winner.bookmarkAndroid": "On Android: open the browser menu and choose Add to bookmarks.",
    "winner.bookmarkMobile": "On mobile, open the browser menu and bookmark this page.",
    "winner.feedback": "Enjoyed it? Your feedback helps me improve DartSync.",
    "feedback.footer": "Give feedback",
    "feedback.celebrate": "Rate DartSync",
    "feedback.title": "Your feedback",
    "feedback.question": "Did you enjoy DartSync?",
    "feedback.up": "I liked it",
    "feedback.down": "I didn't like it",
    "feedback.commentPlaceholder": "Anything to add? (optional)",
    "feedback.send": "Send",
    "feedback.sent": "Thanks, got it.",
    "feedback.error": "Sending failed. Try again in a moment.",
    "feedbackB.celebrate": "How was your game?",
    "feedbackB.hint": "Tell me if the scorer did the job.",
    "feedbackB.title": "Your game",
    "feedbackB.question": "Did the scorer make your game easier?",
    "feedbackB.up": "Yes",
    "feedbackB.down": "Not really",
    "feedbackB.commentPlaceholder": "A word about your game? (optional)",
    "feedbackB.commentPlaceholderUp": "What did you like? (optional)",
    "feedbackB.commentPlaceholderDown": "What got in the way? (optional)",
    "feedbackB.send": "Send",
    "feedbackB.sent": "Thanks, noted.",
    "feedbackB.error": "Sending failed. Try again in a moment.",
    "legal.updated": "Last updated:",

    "settings.close": "Close",
    "settings.stored": "Saved on this device and reused for your next games.",
    "startOrder.noHistory": "No previous game: the order of the list is kept.",
    "checkout.hint": "How to finish the game on exactly 0.",

    "startOrder.label": "Throwing order",
    "startOrder.hint": "Who throws first.",
    "startOrder.preview": "Order of play:",
    "startOrder.previewRandom": "Order drawn when the game starts.",
    "startOrder.fixed.label": "As listed",
    "startOrder.fixed.chip": "LISTED",
    "startOrder.fixed.desc": "The order of the list above",
    "startOrder.random.label": "Random",
    "startOrder.random.chip": "RANDOM",
    "startOrder.random.desc": "Drawn again every game",
    "startOrder.reverse.label": "Loser first",
    "startOrder.reverse.chip": "LOSER",
    "startOrder.reverse.desc": "Last place in the previous game throws first",
    "startOrder.alternate.label": "Rotating",
    "startOrder.alternate.chip": "ROTATING",
    "startOrder.alternate.desc": "Shifts by one player every game",

    "checkout.label": "Checkout rule",
    "checkout.double_out.label": "Double out",
    "checkout.double_out.chip": "DOUBLE",
    "checkout.double_out.desc": "Finish on a double (the bullseye counts). Standard.",
    "checkout.master_out.label": "Master out",
    "checkout.master_out.chip": "MASTER",
    "checkout.master_out.desc": "Finish on a double or a treble.",
    "checkout.straight_out.label": "Straight out",
    "checkout.straight_out.chip": "STRAIGHT",
    "checkout.straight_out.desc": "Finish on any number.",

    // Cricket variant
    "cricket.variant.label": "Variant",
    "cricket.variant.hint": "Who the points you score go to.",
    "cricket.standard.label": "Standard",
    "cricket.standard.chip": "STANDARD",
    "cricket.standard.desc": "Your points add to your score. Highest wins.",
    "cricket.cut_throat.label": "Cut-throat",
    "cricket.cut_throat.chip": "CUT-THROAT",
    "cricket.cut_throat.desc": "Your points go to opponents who haven't closed the number. Lowest wins.",
    "board.lowestWins": "Lowest wins",
    "recap.inflicted": "points given in total!",

    // Round limit and ties
    "rounds.label": "Number of rounds",
    "rounds.hint": "A round is one visit from each player.",
    "rounds.20.label": "20 rounds",
    "rounds.20.chip": "20 ROUNDS",
    "rounds.20.desc": "After round 20, the leader wins.",
    "rounds.unlimited.label": "Unlimited",
    "rounds.unlimited.chip": "∞",
    "rounds.unlimited.desc": "Play until someone wins.",
    "tie.label": "If there is a tie",
    "tie.hint": "Several players level in front after the last round.",
    "tie.unlimited": "With no round limit, there is no tie to settle.",
    "tie.draw.label": "Draw",
    "tie.draw.desc": "The game ends level.",
    "tie.overtime.label": "Play-off",
    "tie.overtime.desc": "The tied players play on, round after round, until one of them goes ahead.",
    "game.round": "ROUND {n}/{max}",
    "game.overtime": "PLAY-OFF",
    "live.overtime": "Tie: play-off.",
    "winner.byRounds": "On points, after {n} rounds.",
    "winner.byPlayoff": "On points, after a play-off.",
    "winner.drawTitle": "Draw",
    "winner.draw": "Level after {n} rounds.",
    "winner.lostTitle": "Lost",
    "winner.lostLeft": "{left} points still to go after {n} rounds.",
    "winner.lostOpen": "After {n} rounds, still open: {targets}.",

    "seo.new.title": "Start an online darts game | DartSync",
    "seo.new.description":
        "Start a game of 501, 301 or Cricket in seconds. Share the link and every player joins without signing up. Live scoring on every phone.",
    "seo.game.title": "Game in progress | DartSync",
    "seo.game.description":
        "The score of the game in progress, live on every connected phone.",

    "seo.notFound.title": "Page not found | DartSync",
    "seo.notFound.description":
        "This page does not exist, or it has moved. Head back home to start a game of darts.",
    "notFound.heading": "Page not found",
    "notFound.body":
        "This page does not exist, or it has moved. Head back home to start a game of darts.",
    "notFound.back": "Back to home",
};

const de = {
    "nav.back": "Zurück",
    "load.failed": "Die Seite konnte nicht geladen werden",
    "load.reload": "Neu laden",
    "agent.recorded": "Vom Assistenten eingegeben: {darts}",
    "agent.undoStale": "Das Spiel ist inzwischen weiter: über das Tastenfeld zurücknehmen.",
    "nav.rules": "Regeln",
    "nav.rules.all": "Alle Regeln",
    "nav.rules.501": "501",
    "nav.rules.301": "301",
    "nav.rules.cricket": "Cricket",
    "nav.multiplayer": "Mehrspieler",
    "nav.checkouts": "Checkouts",
    "nav.guides": "Ratgeber",
    "nav.faq": "FAQ",
    "faq.more": "Weitere Fragen?",
    "faq.seeAll": "Zur vollständigen FAQ",
    "keyAnswer.label": "Kurz gesagt",
    "nav.play": "Spielen",
    "nav.boardSetup": "Abstand & Höhe",
    "nav.stats": "Statistiken",
    "nav.training": "Training",
    "nav.tournament": "Turnier ausrichten",
    "nav.appScore": "Zähler-App",
    "nav.blog": "Blog",
    "nav.menu.open": "Menü öffnen",
    "nav.menu.close": "Menü schließen",
    "footer.col.rules": "Regeln & Maße",
    "footer.col.play": "Spielen",
    "footer.col.progress": "Besser werden",
    "footer.rules501": "501-Regeln",
    "footer.rules301": "301-Regeln",
    "footer.rulesCricket": "Cricket-Regeln",
    "footer.checkoutTable": "Checkout-Tabelle",
    "footer.newGame": "Spiel starten",
    "footer.multiplayer": "Zu mehreren spielen",
    "footer.tagline": "Online Dart-Zähler — 501, 301, Cricket. Kostenlos, ohne Anmeldung.",
    "legal.notice": "Impressum",
    "legal.privacy": "Datenschutz",
    "legal.terms": "AGB",
    "brand.tagline": "Dart-Zähler",
    "lang.label": "Sprache",
    "lang.fr": "Français",
    "lang.en": "English",
    "lang.de": "Deutsch",

    "new.title.line1": "NEUES",
    "new.title.line2": "SPIEL.",
    "new.subtitle": "Erstelle das Spiel, teile den Link, jeder verfolgt den Stand auf dem eigenen Bildschirm.",
    "new.mode.label": "Spielmodus",
    "new.mode.501.desc": "Von 501 auf null",
    "new.mode.301.desc": "Die schnelle Variante",
    "new.mode.cricket.desc": "15-20 + Bull",
    "new.rules.label": "Spielregeln",
    "new.rules.501": "501-Regeln",
    "new.rules.301": "301-Regeln",
    "new.rules.cricket": "Cricket-Regeln",
    "new.players.label": "Spieler",
    "new.players.add": "Hinzufügen",
    "new.players.clear": "Leeren",
    "new.players.cleared": "Liste geleert",
    "new.players.undo": "Rückgängig",
    "new.players.move": "Spieler {n} verschieben (Pfeiltasten auf und ab)",
    "new.players.placeholder": "Spieler {n}",
    "new.advanced": "Einstellungen",
    "new.create": "SPIEL STARTEN",
    "new.creating": "WIRD ERSTELLT...",
    "new.noAccount": "Kein Konto. Keine Anmeldung. Nur ein Link.",
    "new.error.noPlayer": "Füge mindestens einen Spieler hinzu",
    "new.error.duplicateNames": "Zwei Spieler haben denselben Namen: füge eine Initiale hinzu, um sie zu unterscheiden.",
    "new.error.create": "Das Spiel konnte nicht erstellt werden",

    // Ecran de partie
    "game.error.connection": "Keine Verbindung",
    "game.error.generic": "Fehler",
    "game.error.notFound": "Spiel nicht gefunden",
    "game.notFound.title": "SPIEL NICHT GEFUNDEN",
    "game.backHome": "ZUR STARTSEITE",
    "game.leaveLabel": "Spiel verlassen, zurück zur Startseite",
    "game.error.nothingToUndo": "Nichts zurückzunehmen",
    "api.min_one_player": "Füge mindestens einen Spieler hinzu",
    "api.game_not_found": "Spiel nicht gefunden",
    "api.game_over": "Das Spiel ist schon beendet",
    "api.invalid_dart": "Ungültiger Dart-Wert",
    "api.invalid_bull": "Bull gibt es nur als Single (25) oder Doppel (50)",
    "api.invalid_multiplier": "Ungültiger Multiplikator",
    "api.not_your_turn": "{name} ist am Wurf",
    "api.nothing_to_undo": "Nichts zurückzunehmen",
    "game.menu": "Spieloptionen",
    "game.copyLink": "Link kopieren",
    "game.spectator": "Zuschauer-Ansicht",
    "game.scoring": "Eingabe-Ansicht",
    "spectator.autoJoin": "Die Revanche öffnet sich in {n} Sekunden von selbst.",
    "spectator.autoJoinSolo": "Das nächste Spiel öffnet sich in {n} Sekunden von selbst.",
    "game.linkCopied": "Link kopiert",
    "game.copyFailed": "Kopieren nicht möglich",
    "game.otherPlayers": "Andere Spieler",
    "game.showStats": "Statistik anzeigen",
    "game.close": "Schließen",
    "game.nextOrder": "Wurfreihenfolge",
    "game.next": "NÄCHSTER",
    "game.points": "PUNKTE",
    "game.score": "REST",
    "game.wakeLock": "BILDSCHIRM AN",
    "game.nextIs": "Nächster: {name}",
    "keypad.single": "SINGLE",
    "keypad.double": "DOPPEL",
    "keypad.triple": "TRIPLE",
    "keypad.hint": "Multiplikator wählen, dann auf die Zahl tippen.",
    "keypad.undo": "ZURÜCK",
    "live.dart": "{dart}, Rest {left}.",
    "live.miss": "daneben",
    "live.triple": "Triple {n}",
    "live.double": "Doppel {n}",
    "live.bull25": "Single Bull",
    "live.bull50": "Bull",
    "live.undone": "Dart zurückgenommen.",
    "live.volley": "{name}: {total}, Rest {left}.",
    "live.volleyCricket": "Aufnahme von {name} beendet.",
    "live.bust": "{name}: überworfen, Stand unverändert.",
    "live.next": "{name} ist dran, Rest {left}.",
    "live.nextCricket": "{name} ist dran.",
    "dart.miss": "DANEBEN",
    "stats.mpr": "MPR",
    "board.turnOf": "Am Wurf",
    "board.visit": "Aufnahme",
    "board.target": "Ziel",
    "board.points": "Pkt",
    "board.noMark": "keine Markierung",
    "board.oneMark": "1 Markierung",
    "board.marks": "{n} Markierungen",
    "board.closed": "geschlossen",
    "board.closedByAll": "von allen geschlossen",
    "board.bull": "Bull",
    "stats.title": "Statistik",
    "stats.live": "Nach jeder Aufnahme aktualisiert",
    "stats.avg3": "Ø",
    "stats.best": "best",
    "stats.darts": "Darts",
    "recap.bust": "Bust",
    "recap.undo": "ZURÜCK",
    "recap.next": "WEITER",
    "recap.nextTurn": "Am Wurf:",
    "winner.wins": "gewinnt das Spiel.",
    "winner.rematch": "REVANCHE",
    "winner.rematchJoin": "ZUR REVANCHE",
    "winner.replay": "NOCHMAL SPIELEN",
    "winner.replayJoin": "NÄCHSTES SPIEL",
    "winner.newGame": "NEUES SPIEL",
    "recap.done": "Aufnahme beendet",
    "recap.undone": "Aufnahme zurückgenommen",
    "recap.total": "Aufnahme gesamt",
    "recap.score": "Restpunkte",
    "winner.title": "Sieg",
    "winner.stats": "Statistik",
    "winner.avg3": "3-Dart-Ø",
    "winner.darts": "Darts",
    "winner.best": "Beste Aufnahme",
    "winner.showLess": "Weniger anzeigen",
    "winner.showMoreOne": "1 weiteren anzeigen",
    "winner.showMoreMany": "{n} weitere anzeigen",
    "winner.bookmark": "Zu Favoriten hinzufügen",
    "winner.bookmarkShare": "DartSync zu den Favoriten hinzufügen.",
    "winner.bookmarkMac": "Drücke ⌘ + D, um diese Seite zu den Favoriten hinzuzufügen.",
    "winner.bookmarkPc": "Drücke Strg + D, um diese Seite zu den Favoriten hinzuzufügen.",
    "winner.bookmarkIos": "Auf iPhone/iPad: tippe auf Teilen, dann auf Zu Favoriten hinzufügen.",
    "winner.bookmarkAndroid": "Auf Android: öffne das Browsermenü und wähle Zu Lesezeichen hinzufügen.",
    "winner.bookmarkMobile": "Auf dem Handy: öffne das Browsermenü und speichere diese Seite als Lesezeichen.",
    "winner.feedback": "Hat es dir gefallen? Dein Feedback hilft mir, DartSync zu verbessern.",
    "feedback.footer": "Feedback geben",
    "feedback.celebrate": "DartSync bewerten",
    "feedback.title": "Dein Feedback",
    "feedback.question": "Hat dir DartSync gefallen?",
    "feedback.up": "Hat mir gefallen",
    "feedback.down": "Hat mir nicht gefallen",
    "feedback.commentPlaceholder": "Noch etwas hinzuzufügen? (optional)",
    "feedback.send": "Senden",
    "feedback.sent": "Danke, ist angekommen.",
    "feedback.error": "Senden fehlgeschlagen. Versuche es gleich noch einmal.",
    "feedbackB.celebrate": "Wie war dein Spiel?",
    "feedbackB.hint": "Sag mir, ob der Zähler seinen Job gemacht hat.",
    "feedbackB.title": "Dein Spiel",
    "feedbackB.question": "Hat der Zähler dir das Spiel leichter gemacht?",
    "feedbackB.up": "Ja",
    "feedbackB.down": "Nicht wirklich",
    "feedbackB.commentPlaceholder": "Ein Wort zu deinem Spiel? (optional)",
    "feedbackB.commentPlaceholderUp": "Was hat dir gefallen? (optional)",
    "feedbackB.commentPlaceholderDown": "Was hat gehakt? (optional)",
    "feedbackB.send": "Senden",
    "feedbackB.sent": "Danke, notiert.",
    "feedbackB.error": "Senden fehlgeschlagen. Versuche es gleich noch einmal.",
    "legal.updated": "Zuletzt aktualisiert:",

    "settings.close": "Schließen",
    "settings.stored": "Auf diesem Gerät gespeichert und für deine nächsten Spiele übernommen.",
    "startOrder.noHistory": "Kein vorheriges Spiel: die Reihenfolge der Liste bleibt.",
    "checkout.hint": "Wie das Spiel exakt auf null endet.",

    "startOrder.label": "Wurfreihenfolge",
    "startOrder.hint": "Wer zuerst wirft.",
    "startOrder.preview": "Reihenfolge beim Start:",
    "startOrder.previewRandom": "Reihenfolge wird beim Start ausgelost.",
    "startOrder.fixed.label": "Wie eingegeben",
    "startOrder.fixed.chip": "LISTE",
    "startOrder.fixed.desc": "Die Reihenfolge aus der Liste oben",
    "startOrder.random.label": "Zufällig",
    "startOrder.random.chip": "ZUFÄLLIG",
    "startOrder.random.desc": "Vor jedem Spiel neu ausgelost",
    "startOrder.reverse.label": "Verlierer zuerst",
    "startOrder.reverse.chip": "LETZTER",
    "startOrder.reverse.desc": "Der Letzte des vorherigen Spiels wirft zuerst",
    "startOrder.alternate.label": "Rotierend",
    "startOrder.alternate.chip": "ROTATION",
    "startOrder.alternate.desc": "Rückt bei jedem Spiel um einen Spieler weiter",

    "checkout.label": "Checkout-Regel",
    "checkout.double_out.label": "Double Out",
    "checkout.double_out.chip": "DOUBLE",
    "checkout.double_out.desc": "Mit einem Doppel auschecken (das Bullseye zählt). Der Standard.",
    "checkout.master_out.label": "Master Out",
    "checkout.master_out.chip": "MASTER",
    "checkout.master_out.desc": "Mit einem Doppel oder Triple auschecken.",
    "checkout.straight_out.label": "Straight Out",
    "checkout.straight_out.chip": "STRAIGHT",
    "checkout.straight_out.desc": "Mit einem beliebigen Feld auschecken.",

    // Cricket-Variante
    "cricket.variant.label": "Variante",
    "cricket.variant.hint": "An wen deine Punkte gehen.",
    "cricket.standard.label": "Standard",
    "cricket.standard.chip": "STANDARD",
    "cricket.standard.desc": "Deine Punkte zählen für dich. Der höchste Stand gewinnt.",
    "cricket.cut_throat.label": "Cut-Throat",
    "cricket.cut_throat.chip": "CUT-THROAT",
    "cricket.cut_throat.desc": "Deine Punkte gehen an Gegner, die die Zahl noch offen haben. Der niedrigste Stand gewinnt.",
    "board.lowestWins": "Niedrigster gewinnt",
    "recap.inflicted": "Punkte insgesamt verteilt!",

    // Rundenlimit und Gleichstand
    "rounds.label": "Anzahl der Runden",
    "rounds.hint": "Eine Runde ist eine Aufnahme pro Spieler.",
    "rounds.20.label": "20 Runden",
    "rounds.20.chip": "20 RUNDEN",
    "rounds.20.desc": "Nach der 20. Runde gewinnt, wer vorn liegt.",
    "rounds.unlimited.label": "Unbegrenzt",
    "rounds.unlimited.chip": "∞",
    "rounds.unlimited.desc": "Gespielt wird bis zum Sieg.",
    "tie.label": "Bei Gleichstand",
    "tie.hint": "Mehrere Spieler vorn nach der letzten Runde.",
    "tie.unlimited": "Ohne Rundenlimit gibt es keinen Gleichstand zu entscheiden.",
    "tie.draw.label": "Unentschieden",
    "tie.draw.desc": "Die Partie endet mit dem Gleichstand.",
    "tie.overtime.label": "Stechen",
    "tie.overtime.desc": "Die Gleichplatzierten spielen Runde um Runde weiter, bis einer vorn liegt.",
    "game.round": "RUNDE {n}/{max}",
    "game.overtime": "STECHEN",
    "live.overtime": "Gleichstand: Stechen.",
    "winner.byRounds": "Nach Punkten, nach {n} Runden.",
    "winner.byPlayoff": "Nach Punkten, nach dem Stechen.",
    "winner.drawTitle": "Unentschieden",
    "winner.draw": "Gleichstand nach {n} Runden.",
    "winner.lostTitle": "Verloren",
    "winner.lostLeft": "Nach {n} Runden noch {left} Punkte übrig.",
    "winner.lostOpen": "Nach {n} Runden noch offen: {targets}.",

    "seo.new.title": "Dart-Spiel online starten | DartSync",
    "seo.new.description":
        "Starte in Sekunden ein 501, 301 oder Cricket. Teile den Link, jeder Spieler kommt ohne Anmeldung dazu. Punktestand live auf jedem Handy.",
    "seo.game.title": "Spiel läuft | DartSync",
    "seo.game.description":
        "Der Punktestand des laufenden Spiels, live auf allen verbundenen Handys.",

    "seo.notFound.title": "Seite nicht gefunden | DartSync",
    "seo.notFound.description":
        "Diese Seite gibt es nicht oder sie wurde verschoben. Zurück zur Startseite, um ein Dart-Spiel zu starten.",
    "notFound.heading": "Seite nicht gefunden",
    "notFound.body":
        "Diese Seite gibt es nicht oder sie wurde verschoben. Zurück zur Startseite, um ein Dart-Spiel zu starten.",
    "notFound.back": "Zurück zur Startseite",
};

export const STRINGS = { fr, en, de };

/**
 * La chaîne demandée, avec ses variables `{nom}` remplacées.
 * Une variable absente reste visible sous sa forme `{nom}` : muette, elle
 * laisserait passer un « Joueur  » sans numéro jusqu'en production.
 */
const own = (table, key) =>
    Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

export function translate(lang, key, vars) {
    // hasOwnProperty et non l'acces direct : translate("en", "toString")
    // renvoyait la fonction du prototype, que React refuse de rendre.
    const table = own(STRINGS, lang) || STRINGS.fr;
    const raw = own(table, key) ?? own(STRINGS.fr, key) ?? key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (match, name) =>
        vars[name] === undefined ? match : String(vars[name])
    );
}
